import type { AgentsBoard } from '../../../api/agentSchemas';
import type { BusinessReport, PnlResult, ReportSalesTotals } from '../../../api/schemas';
import { formatMoneyCents } from '../../../lib/format';
import { periodLabel } from '../../../lib/reports';
import { spoken, type AgentLine, type Figure } from '../agentLine';
import type { LineId } from '../voice/lines';
import type { FinanceReady, ReportKey } from './facts';

/** What a finance answer is built from: the numbers that were fetched and the team's board. */
export type ReplyContext = { facts: FinanceReady; board: AgentsBoard };
export type Reply = (context: ReplyContext) => AgentLine;

type Extras = Parameters<typeof spoken>[2];

/** Mercurio speaks every finance line. */
export function mercurio(voz: LineId, extras?: Extras): AgentLine {
  return spoken('finanzas', voz, extras);
}

/** The numbers a question needed are missing: say so rather than guess. */
export function failure(): AgentLine {
  return mercurio('error-cuentas');
}

export function onReport(key: ReportKey, build: (report: BusinessReport, context: ReplyContext) => AgentLine): Reply {
  return (context) => {
    const report = context.facts.reports[key];
    return report ? build(report, context) : failure();
  };
}

export function onPnl(build: (pnl: PnlResult, context: ReplyContext) => AgentLine): Reply {
  return (context) => (context.facts.pnl ? build(context.facts.pnl, context) : failure());
}

/** One row of money; a negative amount is marked so it reads as a loss. */
export function money(label: string, cents: number): Figure {
  return cents < 0 ? { label, value: formatMoneyCents(cents), tone: 'negative' } : { label, value: formatMoneyCents(cents) };
}

export function count(label: string, value: number): Figure {
  return { label, value: String(value) };
}

/** Ingresos, and, once expenses are written down, Gastos and what is left. */
export function incomeFigures(totals: ReportSalesTotals): Figure[] {
  const income = money('Ingresos', totals.revenueCents);
  if (totals.expenseCents <= 0) return [income];
  return [income, money('Gastos', totals.expenseCents), money('Te queda', totals.resultCents)];
}

export function hasMoved(totals: ReportSalesTotals): boolean {
  return totals.revenueCents > 0 || totals.expenseCents > 0;
}

export function labelOf(period: { from: string; to: string }): string {
  return periodLabel(period);
}

/** The biggest `limit` entries with something in them, biggest first; the input is left alone. */
export function biggest<T>(items: readonly T[], amount: (item: T) => number, limit: number): T[] {
  return items
    .filter((item) => amount(item) > 0)
    .toSorted((a, b) => amount(b) - amount(a))
    .slice(0, limit);
}
